import { useRef, useState } from 'react';

import Box from 'shopper/components/Box';
import { TabNav, TabNavItem } from 'shopper/components/TabNav';

import useInfiniteQuery from 'hooks/useInfiniteQuery';
import useIntersectionObserver from 'hooks/useIntersectionObserver';

import { sendEvent } from 'lib/gtag';
import { userQueryKeys } from 'lib/queryKeys';

import { getUserCommentsList } from 'services/comment';
import {
  getUserOffers,
  getUserReviewsList,
  getUserThreadsList,
} from 'services/user';

import InteractionsEmptyList from './InteractionsEmptyList';
import InteractionsList from './InteractionsList';
import InteractionsListSpinner from './InteractionsListSpinner';

import APP from 'constants/app';
import LINKS from 'constants/links';

const INITIAL_TAB_INDEX = 0;

const TABS = [
  {
    title: 'Reviews',
    name: 'review',
    id: 'reviews',
    flattenKey: 'reviews',
    fetcher: getUserReviewsList,
    emptyState: {
      description: 'Nenhuma review encontrada',
      image: `${APP.LOCAL_IMAGE_PATH}/features/user-profile/empty-tab-reviews.svg`,
      callToAction: {
        text: null,
        href: null,
      },
    },
  },
  {
    title: 'Comentários',
    name: 'comentários',
    id: 'comments',
    flattenKey: 'comments',
    fetcher: getUserCommentsList,
    emptyState: {
      description: 'Nenhum comentário encontrado',
      image: `${APP.LOCAL_IMAGE_PATH}/features/user-profile/empty-tab-comments.svg`,
      callToAction: {
        text: null,
        href: null,
      },
    },
  },
  {
    title: 'Tópicos',
    name: 'tópicos',
    id: 'threads',
    flattenKey: 'threads',
    fetcher: getUserThreadsList,
    emptyState: {
      description: 'Nenhum tópico encontrado',
      image: `${APP.LOCAL_IMAGE_PATH}/features/user-profile/empty-tab-reviews.svg`,
      callToAction: {
        text: 'Crie seu primeiro tópico',
        href: LINKS.NEW_THREAD,
      },
    },
  },
  {
    title: 'Ofertas',
    name: 'ofertas',
    id: 'offers',
    flattenKey: 'offers',
    fetcher: getUserOffers,
    emptyState: {
      description: 'Nenhuma oferta encontrada',
      image: `${APP.LOCAL_IMAGE_PATH}/features/user-profile/empty-tab-offers.svg`,
      callToAction: {
        text: 'Envie sua primeira oferta',
        href: LINKS.NEW_OFFER,
      },
    },
  },
];

const Interactions = ({ gaEventCategory, userId }) => {
  const [activeTab, setActiveTab] = useState(TABS[INITIAL_TAB_INDEX]);
  const [isScrollPaginationEnabled, setScrollPaginationEnabled] =
    useState(false);
  const fetchNextPageRef = useRef(null);

  const {
    data: lastInteractions,
    isSuccess,
    isLoading,
    hasNextPage,
    isFetching,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: userQueryKeys.interactionsList({
      userId,
      activeTabId: activeTab.id,
    }),
    queryFn: (after, { signal }) =>
      activeTab.fetcher({ userId, after }, { signal }),
    getNextPageParam: (interactions) => interactions?.after,
    flattenKey: activeTab.flattenKey,
    cacheTime: 0,
    staleTime: 0,
  });

  useIntersectionObserver({
    enabled: isScrollPaginationEnabled && hasNextPage && !isFetching,
    target: fetchNextPageRef,
    rootMargin: '500px',
    threshold: 0,
    onIntersect: fetchNextPage,
  });

  const onTabClick = (tab) => {
    sendEvent({ category: gaEventCategory, action: `${tab.id}_tab` });
    setActiveTab(tab);
    setScrollPaginationEnabled(false);
  };

  const onInteractionsListLoadMoreButtonClick = () => {
    sendEvent({
      category: gaEventCategory,
      action: `${activeTab.id}_load_more`,
    });
    setScrollPaginationEnabled(true);
  };

  return (
    <Box>
      <TabNav>
        {TABS.map((tab) => (
          <TabNavItem
            key={tab.id}
            active={tab.title === activeTab.title}
            as="a"
            className="w-1/4 p-4"
            onClick={() => {
              onTabClick(tab);
            }}
          >
            {tab.title}
          </TabNavItem>
        ))}
      </TabNav>
      {!isLoading && isSuccess && lastInteractions.length > 0 && (
        <>
          <InteractionsList
            activeTabId={activeTab.id}
            hasNextPage={hasNextPage}
            interactions={lastInteractions}
            isScrollPaginationEnabled={isScrollPaginationEnabled}
            onLoadMoreButtonClick={onInteractionsListLoadMoreButtonClick}
          />
          <div ref={fetchNextPageRef} />
        </>
      )}
      {!isLoading && isSuccess && lastInteractions.length === 0 && (
        <InteractionsEmptyList tab={activeTab} userId={userId} />
      )}
      {(isLoading || (isFetching && !isSuccess)) && (
        <InteractionsListSpinner className={isFetching ? 'py-16' : 'py-3'} />
      )}
    </Box>
  );
};

export default Interactions;
